var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CONFIG } from "@/config";
import { getObjectByName, getOptionalObjectByName, listMeshes, } from "@/utils/three";
import { useFrame } from "@react-three/fiber";
import { useAtom } from "jotai";
import { useAtomValue } from "jotai";
import { clamp, values } from "lodash";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { AnimationMixer, BackSide, DoubleSide, FrontSide, LoopOnce, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, Quaternion, sRGBEncoding, Vector3, } from "three";
import { ArtbookLocale, CameraAtom } from "../atoms";
import { useGLTFWithCORS, useTextureWithCORS, } from "../utils/three-loader-hooks";
import { convertRangeClamped } from "@/components/CameraControl";
export function Table(_a) {
    var scale = _a.scale, position = _a.position, onProgress = _a.onProgress, onRightPagePos = _a.onRightPagePos;
    var locale = useAtomValue(ArtbookLocale);
    // Loading both covers, otherwise the reloading appear glitchy
    var coverTextures = useTextureWithCORS(CONFIG.textures.locales.reduce(function (covers, localeTextures) {
        var _a;
        return (__assign(__assign({}, covers), (_a = {}, _a[localeTextures.name] = localeTextures.bookCover.src, _a)));
    }, {}));
    var coverTexture = coverTextures[locale];
    useLayoutEffect(function () {
        Object.values(coverTextures).forEach(function (texture) {
            texture.flipY = false;
            texture.encoding = sRGBEncoding;
        });
    }, [coverTextures]);
    var gltf = useGLTFWithCORS(CONFIG.models.artbook.file);
    var meshes = useMemo(function () { return listMeshes(gltf.scene.children); }, [gltf]);
    useLayoutEffect(function () {
        var e_1, _a, e_2, _b, e_3, _c;
        try {
            for (var _d = __values(values(meshes)), _e = _d.next(); !_e.done; _e = _d.next()) {
                var node = _e.value;
                if (node.material instanceof MeshStandardMaterial ||
                    node.material instanceof MeshBasicMaterial) {
                    node.material = new MeshBasicMaterial({
                        map: node.material.map,
                        color: 0xffffff,
                        side: CONFIG.models.artbook.doubleSideNode.indexOf(node.name) !== -1
                            ? DoubleSide
                            : FrontSide,
                    });
                }
                else {
                    console.error("".concat(node.name, " doesn't have a standard material"), node.material);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_1) throw e_1.error; }
        }
        // FIXME: should use getObjectByName
        var shadowOverlay = CONFIG.models.artbook.shadowOverlay != null
            ? getOptionalObjectByName(gltf.scene, CONFIG.models.artbook.shadowOverlay, Mesh)
            : null;
        if (shadowOverlay) {
            shadowOverlay.material.side = BackSide;
            shadowOverlay.material.depthTest = false;
        }
        var shadows = CONFIG.models.artbook.shadows;
        try {
            for (var shadows_1 = __values(shadows), shadows_1_1 = shadows_1.next(); !shadows_1_1.done; shadows_1_1 = shadows_1.next()) {
                var name = shadows_1_1.value;
                var mesh = getObjectByName(gltf.scene, name, Mesh);
                mesh.renderOrder = 10;
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (shadows_1_1 && !shadows_1_1.done && (_b = shadows_1.return)) _b.call(shadows_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        var transparentNodes = __spreadArray(__spreadArray([], __read(shadows), false), __read(CONFIG.models.artbook.transparentNodes), false);
        try {
            for (var transparentNodes_1 = __values(transparentNodes), transparentNodes_1_1 = transparentNodes_1.next(); !transparentNodes_1_1.done; transparentNodes_1_1 = transparentNodes_1.next()) {
                var name = transparentNodes_1_1.value;
                getObjectByName(gltf.scene, name, Mesh).material.transparent = true;
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (transparentNodes_1_1 && !transparentNodes_1_1.done && (_c = transparentNodes_1.return)) _c.call(transparentNodes_1);
            }
            finally { if (e_3) throw e_3.error; }
        }
    }, [gltf, meshes]);
    useEffect(function () {
        if (CONFIG.models.artbook.top == null)
            return;
        var topMesh = getObjectByName(gltf.scene, CONFIG.models.artbook.top, Mesh);
        topMesh.material.side = FrontSide;
        topMesh.material.map = coverTexture;
    }, [gltf, coverTexture]);
    var _b = useMemo(function () {
        var mixer = new AnimationMixer(gltf.scene);
        var action = mixer.clipAction(gltf.animations[0]);
        action.clampWhenFinished = true;
        action.loop = LoopOnce;
        return { mixer: mixer, action: action };
    }, [gltf]), mixer = _b.mixer, action = _b.action;
    var _c = __read(useAtom(CameraAtom), 2), cameraAnimation = _c[0], updateCamera = _c[1];
    useEffect(function () {
        action.timeScale = 0;
        action.play();
    }, [action]);
    useEffect(function () {
        function play() {
            if (cameraAnimation.intro.triggered &&
                cameraAnimation.intro.progress == 0 &&
                !action.isRunning()) {
                // TODO: refacto timing config
                if (CONFIG.skipIntroAnimation) {
                    action.time = 2.5;
                }
                action.timeScale = 1;
                action.play();
            }
        }
        if (process.env.CAPTURE_CONTROLS === "true") {
            var listener_1 = function (e) {
                if (e.code === "KeyP") {
                    play();
                }
            };
            window.addEventListener("keydown", listener_1);
            return function () {
                window.removeEventListener("keydown", listener_1);
            };
        }
        else {
            play();
        }
    }, [action, cameraAnimation.intro]);
    var lastTime = useRef({ now: null });
    useFrame(function (_a) {
        var clock = _a.clock;
        var now = clock.getElapsedTime();
        if (lastTime.current.now != null) {
            var dt = now - lastTime.current.now;
            mixer.update(dt);
        }
        lastTime.current.now = now;
        // TODO: refacto timing config
        var introDuration = CONFIG.skipIntroAnimation
            ? action.getClip().duration - 4
            : action.getClip().duration;
        var progress = clamp(action.time / introDuration, 0, 1);
        var camera = getObjectByName(gltf.scene, CONFIG.models.artbook.camera, Object3D);
        updateCamera({
            action: "progressIntro",
            progress: progress,
        });
        updateCamera({
            action: "move",
            position: camera.getWorldPosition(new Vector3(0, 0, 0)),
            rotation: camera.getWorldQuaternion(new Quaternion()),
        });
        onProgress === null || onProgress === void 0 ? void 0 : onProgress(progress);
        var pageAnchor = CONFIG.models.artbook.pageAnchor;
        if (typeof pageAnchor === "string") {
            var rightPage = getObjectByName(gltf.scene, pageAnchor, Object3D);
            onRightPagePos === null || onRightPagePos === void 0 ? void 0 : onRightPagePos(rightPage.getWorldPosition(new Vector3(0, 0, 0)).x);
        }
        else if (Array.isArray(pageAnchor)) {
            var x = convertRangeClamped(progress, pageAnchor[0], pageAnchor[1]);
            onRightPagePos === null || onRightPagePos === void 0 ? void 0 : onRightPagePos(x);
        }
    });
    return (React.createElement("group", { scale: scale, position: position },
        React.createElement("primitive", { object: gltf.scene })));
}
